import { GenericServiceBase } from '../generic-service';

/** Situações publicadas pelo servidor em `LIGACAO_ATUALIZADA`. */
export type SituacaoLigacao = 'TOCANDO' | 'ATENDIDA' | 'PERDIDA' | 'RECUSADA' | 'ENCERRADA';

/**
 * Quem originou a ligação.
 *
 * NÃO é cosmético: `RECEBIDA` é a única que pode ser ATENDIDA. Numa `REALIZADA`
 * quem discou já é o dono (o servidor grava a posse no próprio `discar`), então
 * oferecer "Atender" para ela devolve 409 — `assumir` exige `id_usuario IS NULL`
 * — e ainda derruba o áudio que a discagem tinha acabado de abrir.
 */
export type DirecaoLigacao = 'RECEBIDA' | 'REALIZADA';

/**
 * Payload de `LIGACAO_ATUALIZADA`.
 *
 * `idContato` e `idAtendimento` são NULÁVEIS de propósito: ligação de número sem
 * contato cadastrado, ou sem conversa aberta, é justamente o caso que a feature
 * existe para mostrar. O front não pode assumir que existe chat para abrir.
 */
export type LigacaoAtualizadaPayload = {
    idInbox:        string;
    idLigacao:      string;
    idContato:      string | null;
    idAtendimento:  string | null;
    codigo:         string;
    nome:           string | null;
    /**
     * Foto do contato. Vem no evento porque a ligação pode ser de quem não tem
     * conversa aberta — não há chat carregado de onde tirar a imagem. Opcional
     * por compatibilidade com servidor antigo.
     */
    imagem?:        string | null;
    situacao:       SituacaoLigacao;
    motivo:         string | null;
    ehVideo:        boolean;
    data:           string;
    /**
     * Direção. Opcional só por compatibilidade com servidor antigo (que não
     * publicava o campo); o front trata ausência como `RECEBIDA`.
     */
    direcao?:       DirecaoLigacao;
    /** Dono da ligação — quem atendeu, ou quem discou. `null` enquanto toca. */
    idUsuario?:     string | null;
    /** Atendentes vinculados ao atendimento da ligação. */
    atendentes?:    string[];
    /**
     * Quando foi atendida. É a ÂNCORA do cronômetro do card: o front conta a
     * partir dela em vez de contar desde que recebeu o evento, senão a
     * contagem reiniciaria a cada reload no meio da ligação.
     */
    dataAtendimento?: string | null;
    /** Quando terminou. `null` até o estado terminal. */
    dataFim?:         string | null;
    /** Tempo de fala em segundos. `null` = não houve fala. */
    duracaoSegundos?: number | null;
};

/**
 * Resposta de `discar`.
 *
 * É a linha de `ligacoes` inteira, não `{ callId }`: o id que todas as outras
 * rotas usam é o `idExterno` (o `call-id` do WhatsApp), e é dele que a sessão
 * de áudio precisa em seguida.
 */
export type LigacaoDiscada = {
    id:            string;
    idExterno:     string;
    idInbox:       string;
    idContato:     string | null;
    idAtendimento: string | null;
    idUsuario:     string | null;
    direcao:       DirecaoLigacao;
    situacao:      SituacaoLigacao;
    motivo:        string | null;
    ehVideo:       boolean;
    dataInicio:    string;
};

/** Resposta de `sessao` — o ticket do WebSocket de áudio. */
export type SessaoLigacao = {
    callId:    string;
    peer:      string;
    /** Caminho do WebSocket, SEM o token. Sempre presente. */
    path:      string;
    /**
     * Token que autoriza este áudio. Enviado como subprotocolo do WebSocket
     * (header `Sec-WebSocket-Protocol`), nunca na URL — query string cai no log
     * do proxy reverso e no histórico do browser.
     */
    token:     string;
    /**
     * URL absoluta do WebSocket, já em `ws://`/`wss://`.
     *
     * Composta pelo WHIZ-SERVER a partir da `baseUrl` do servidor Evolution —
     * que é a fonte de verdade e sobrevive a transferência de inbox. O gateway
     * não se auto-anuncia. Ausente só se o servidor não tiver baseUrl gravada.
     */
    url?:      string;
    expiresIn: number;
};

export class LigacoesService extends GenericServiceBase {
    constructor() { super('ligacoes'); }

    /**
     * Emite o token de áudio. Chamar ANTES de `atender`: o WebSocket precisa
     * estar aberto quando a ligação é aceita, senão quem ligou ouve silêncio e o
     * operador perde o começo da fala.
     */
    async abrirSessao(idInbox: string, idLigacao: string): Promise<SessaoLigacao> {
        return await this.f.post(`/${idInbox}/${idLigacao}/sessao`, {}) as SessaoLigacao;
    }

    async atender(idInbox: string, idLigacao: string): Promise<void> {
        await this.f.post(`/${idInbox}/${idLigacao}/atender`, {});
    }

    async recusar(idInbox: string, idLigacao: string): Promise<void> {
        await this.f.post(`/${idInbox}/${idLigacao}/recusar`, {});
    }

    async encerrar(idInbox: string, idLigacao: string): Promise<void> {
        await this.f.post(`/${idInbox}/${idLigacao}/encerrar`, {});
    }

    /**
     * Ligações VIVAS das inboxes do usuário, no mesmo formato do evento do
     * socket — por isso o retorno é `LigacaoAtualizadaPayload[]` e não um tipo
     * próprio: cada item entra pelo mesmo `aplicar()` do evento.
     *
     * Existe porque o evento não tem replay. Quem abre o app depois de a ligação
     * começar (recarregou a página, tocou no push, o app estava fechado) não
     * recebe nada do socket sobre o que já estava tocando.
     */
    async ativas(): Promise<LigacaoAtualizadaPayload[]> {
        return await this.f.get('/ativas') as LigacaoAtualizadaPayload[];
    }

    /**
     * Disca. `idAtendimento` amarra a ligação à conversa aberta; omitido, o
     * servidor reusa o atendimento aberto do contato (e cria um se não houver).
     */
    async discar(idInbox: string, numero: string, idAtendimento?: string | null): Promise<LigacaoDiscada> {
        const corpo: Record<string, string> = { numero };
        if (idAtendimento) corpo.idAtendimento = idAtendimento;
        return await this.f.post(`/${idInbox}/discar`, corpo) as LigacaoDiscada;
    }
}

export const ligacoesService = new LigacoesService();
